import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const UIStateContext = createContext(null);

const BASE_STORAGE_KEY = 'service_wizard_ui_state';

// Helper function to decode JWT token and extract company_id
const getCompanyIdFromToken = () => {
  try {
    const token = sessionStorage.getItem('token');
    if (!token) return null;

    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const payload = JSON.parse(jsonPayload);
    return payload.company_id;
  } catch (error) {
    console.error('[UIState] Failed to decode token:', error);
    return null;
  }
};

// Get company-specific storage key
const getStorageKey = () => {
  const companyId = getCompanyIdFromToken();
  return companyId ? `${BASE_STORAGE_KEY}_${companyId}` : BASE_STORAGE_KEY;
};

const DEFAULT_STATE = {
  // Chat/Home state
  chat: {
    lastConversationId: null,
  },
  // Knowledge page state
  knowledge: {
    selectedCollections: [],
    lockedCollectionId: null,
    searchTerm: '',
    selectedProduct: 'all',
    selectedCategory: 'all',
    contextFilters: [],
    sharedSlotPositions: {},
    // Document import progress tracking
    importProgress: null, // { fileName, status: 'uploading'|'processing'|'importing'|'complete'|'error', progress: 0-100, error: null, result: null }
  },
  // Admin page state
  admin: {
    activeTab: 'users',
    selectedConfigId: null,
    selectedUserId: null,
    selectedInvitationId: null,
    selectedProductId: null,
    selectedRoleId: null,
    selectedPermissionId: null,
    // Per-model configuration states
    configStates: {},
    // Per-role states (track active tab for each role)
    roleStates: {},
    // Per-permission states (track active tab for each permission)
    permissionStates: {},
  },
};

/**
 * UI State Provider
 *
 * Manages and persists UI state across browser refreshes using localStorage.
 * Tracks state for different pages: chat, knowledge, and admin.
 */
export function UIStateProvider({ children }) {
  const [state, setState] = useState(() => {
    try {
      const storageKey = getStorageKey();
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge with defaults to handle new properties
        return {
          chat: { ...DEFAULT_STATE.chat, ...parsed.chat },
          knowledge: { ...DEFAULT_STATE.knowledge, ...parsed.knowledge },
          admin: { ...DEFAULT_STATE.admin, ...parsed.admin },
        };
      }
    } catch (error) {
      console.error('[UIState] Failed to load state from localStorage:', error);
    }
    return DEFAULT_STATE;
  });

  // Save to localStorage whenever state changes (with company-specific key)
  useEffect(() => {
    try {
      const storageKey = getStorageKey();
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (error) {
      console.error('[UIState] Failed to save state to localStorage:', error);
    }
  }, [state]);

  // ============================================================================
  // CHAT STATE MANAGEMENT
  // ============================================================================

  const setChatLastConversation = useCallback((conversationId) => {
    setState(prev => ({
      ...prev,
      chat: {
        ...prev.chat,
        lastConversationId: conversationId,
      },
    }));
  }, []);

  const getChatLastConversation = useCallback(() => {
    return state.chat.lastConversationId;
  }, [state.chat.lastConversationId]);

  // ============================================================================
  // KNOWLEDGE STATE MANAGEMENT
  // ============================================================================

  const setKnowledgeSelectedCollections = useCallback((collections) => {
    setState(prev => ({
      ...prev,
      knowledge: {
        ...prev.knowledge,
        selectedCollections: Array.isArray(collections) ? collections : [],
      },
    }));
  }, []);

  const setKnowledgeLockedCollection = useCallback((collectionId) => {
    setState(prev => ({
      ...prev,
      knowledge: {
        ...prev.knowledge,
        lockedCollectionId: collectionId,
      },
    }));
  }, []);

  const setKnowledgeSearchTerm = useCallback((searchTerm) => {
    setState(prev => ({
      ...prev,
      knowledge: {
        ...prev.knowledge,
        searchTerm: searchTerm || '',
      },
    }));
  }, []);

  const setKnowledgeSelectedProduct = useCallback((productId) => {
    setState(prev => ({
      ...prev,
      knowledge: {
        ...prev.knowledge,
        selectedProduct: productId || 'all',
      },
    }));
  }, []);

  const setKnowledgeSelectedCategory = useCallback((category) => {
    setState(prev => ({
      ...prev,
      knowledge: {
        ...prev.knowledge,
        selectedCategory: category || 'all',
      },
    }));
  }, []);

  const setKnowledgeContextFilters = useCallback((filters) => {
    setState(prev => ({
      ...prev,
      knowledge: {
        ...prev.knowledge,
        contextFilters: Array.isArray(filters) ? filters : [],
      },
    }));
  }, []);

  const setKnowledgeSharedSlotPositions = useCallback((positions) => {
    setState(prev => ({
      ...prev,
      knowledge: {
        ...prev.knowledge,
        sharedSlotPositions: positions || {},
      },
    }));
  }, []);

  const getKnowledgeState = useCallback(() => {
    return state.knowledge;
  }, [state.knowledge]);

  const setKnowledgeImportProgress = useCallback((progress) => {
    setState(prev => ({
      ...prev,
      knowledge: {
        ...prev.knowledge,
        importProgress: progress,
      },
    }));
  }, []);

  const getKnowledgeImportProgress = useCallback(() => {
    return state.knowledge.importProgress;
  }, [state.knowledge.importProgress]);

  // Clear knowledge state (useful when navigating away)
  const clearKnowledgeState = useCallback(() => {
    setState(prev => ({
      ...prev,
      knowledge: { ...DEFAULT_STATE.knowledge },
    }));
  }, []);

  // ============================================================================
  // ADMIN STATE MANAGEMENT
  // ============================================================================

  const setAdminActiveTab = useCallback((tab) => {
    setState(prev => ({
      ...prev,
      admin: {
        ...prev.admin,
        activeTab: tab || 'users',
      },
    }));
  }, []);

  const getAdminActiveTab = useCallback(() => {
    return state.admin.activeTab;
  }, [state.admin.activeTab]);

  const setAdminSelectedConfig = useCallback((configId) => {
    setState(prev => ({
      ...prev,
      admin: {
        ...prev.admin,
        selectedConfigId: configId,
      },
    }));
  }, []);

  const getAdminSelectedConfig = useCallback(() => {
    return state.admin.selectedConfigId;
  }, [state.admin.selectedConfigId]);

  const setAdminSelectedUser = useCallback((userId) => {
    setState(prev => ({
      ...prev,
      admin: {
        ...prev.admin,
        selectedUserId: userId,
      },
    }));
  }, []);

  const getAdminSelectedUser = useCallback(() => {
    return state.admin.selectedUserId;
  }, [state.admin.selectedUserId]);

  const setAdminSelectedInvitation = useCallback((invitationId) => {
    setState(prev => ({
      ...prev,
      admin: {
        ...prev.admin,
        selectedInvitationId: invitationId,
      },
    }));
  }, []);

  const getAdminSelectedInvitation = useCallback(() => {
    return state.admin.selectedInvitationId;
  }, [state.admin.selectedInvitationId]);

  const setAdminSelectedProduct = useCallback((productId) => {
    setState(prev => ({
      ...prev,
      admin: {
        ...prev.admin,
        selectedProductId: productId,
      },
    }));
  }, []);

  const getAdminSelectedProduct = useCallback(() => {
    return state.admin.selectedProductId;
  }, [state.admin.selectedProductId]);

  const setAdminSelectedRole = useCallback((roleId) => {
    setState(prev => ({
      ...prev,
      admin: {
        ...prev.admin,
        selectedRoleId: roleId,
      },
    }));
  }, []);

  const getAdminSelectedRole = useCallback(() => {
    return state.admin.selectedRoleId;
  }, [state.admin.selectedRoleId]);

  const setAdminSelectedPermission = useCallback((permissionId) => {
    setState(prev => ({
      ...prev,
      admin: {
        ...prev.admin,
        selectedPermissionId: permissionId,
      },
    }));
  }, []);

  const getAdminSelectedPermission = useCallback(() => {
    return state.admin.selectedPermissionId;
  }, [state.admin.selectedPermissionId]);

  const setAdminDetailTab = useCallback((configId, tab) => {
    if (!configId) return;
    setState(prev => ({
      ...prev,
      admin: {
        ...prev.admin,
        configStates: {
          ...prev.admin.configStates,
          [configId]: {
            ...prev.admin.configStates[configId],
            selectedDetailTab: tab || 'configuration',
          },
        },
      },
    }));
  }, []);

  const getAdminDetailTab = useCallback((configId) => {
    if (!configId) return 'configuration';
    return state.admin.configStates[configId]?.selectedDetailTab || 'configuration';
  }, [state.admin.configStates]);

  const setAdminSubTab = useCallback((configId, subTab) => {
    if (!configId) return;
    setState(prev => ({
      ...prev,
      admin: {
        ...prev.admin,
        configStates: {
          ...prev.admin.configStates,
          [configId]: {
            ...prev.admin.configStates[configId],
            selectedSubTab: subTab || 'configuration',
          },
        },
      },
    }));
  }, []);

  const getAdminSubTab = useCallback((configId) => {
    if (!configId) return 'configuration';
    return state.admin.configStates[configId]?.selectedSubTab || 'configuration';
  }, [state.admin.configStates]);

  const setAdminSelectedItem = useCallback((configId, itemId) => {
    if (!configId) return;
    setState(prev => ({
      ...prev,
      admin: {
        ...prev.admin,
        configStates: {
          ...prev.admin.configStates,
          [configId]: {
            ...prev.admin.configStates[configId],
            selectedItemId: itemId,
          },
        },
      },
    }));
  }, []);

  const getAdminSelectedItem = useCallback((configId) => {
    if (!configId) return null;
    return state.admin.configStates[configId]?.selectedItemId || null;
  }, [state.admin.configStates]);

  // Role tab state management (per-role active tab tracking)
  const setAdminRoleTab = useCallback((roleId, tab) => {
    if (!roleId) return;
    setState(prev => ({
      ...prev,
      admin: {
        ...prev.admin,
        roleStates: {
          ...prev.admin.roleStates,
          [roleId]: {
            ...prev.admin.roleStates[roleId],
            selectedTab: tab || 'details',
          },
        },
      },
    }));
  }, []);

  const getAdminRoleTab = useCallback((roleId) => {
    if (!roleId) return 'details';
    return state.admin.roleStates[roleId]?.selectedTab || 'details';
  }, [state.admin.roleStates]);

  // Permission tab state management (per-permission active tab tracking)
  const setAdminPermissionTab = useCallback((permissionId, tab) => {
    if (!permissionId) return;
    setState(prev => ({
      ...prev,
      admin: {
        ...prev.admin,
        permissionStates: {
          ...prev.admin.permissionStates,
          [permissionId]: {
            ...prev.admin.permissionStates[permissionId],
            selectedTab: tab || 'details',
          },
        },
      },
    }));
  }, []);

  const getAdminPermissionTab = useCallback((permissionId) => {
    if (!permissionId) return 'details';
    return state.admin.permissionStates[permissionId]?.selectedTab || 'details';
  }, [state.admin.permissionStates]);

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  const clearAllState = useCallback(() => {
    setState(DEFAULT_STATE);
    try {
      const storageKey = getStorageKey();
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error('[UIState] Failed to clear localStorage:', error);
    }
  }, []);

  const value = {
    // Chat
    setChatLastConversation,
    getChatLastConversation,
    // Knowledge
    setKnowledgeSelectedCollections,
    setKnowledgeLockedCollection,
    setKnowledgeSearchTerm,
    setKnowledgeSelectedProduct,
    setKnowledgeSelectedCategory,
    setKnowledgeContextFilters,
    setKnowledgeSharedSlotPositions,
    setKnowledgeImportProgress,
    getKnowledgeImportProgress,
    getKnowledgeState,
    clearKnowledgeState,
    // Admin
    setAdminActiveTab,
    getAdminActiveTab,
    setAdminSelectedConfig,
    getAdminSelectedConfig,
    setAdminSelectedUser,
    getAdminSelectedUser,
    setAdminSelectedInvitation,
    getAdminSelectedInvitation,
    setAdminSelectedProduct,
    getAdminSelectedProduct,
    setAdminSelectedRole,
    getAdminSelectedRole,
    setAdminSelectedPermission,
    getAdminSelectedPermission,
    setAdminDetailTab,
    getAdminDetailTab,
    setAdminSubTab,
    getAdminSubTab,
    setAdminSelectedItem,
    getAdminSelectedItem,
    setAdminRoleTab,
    getAdminRoleTab,
    setAdminPermissionTab,
    getAdminPermissionTab,
    // Utility
    clearAllState,
  };

  return (
    <UIStateContext.Provider value={value}>
      {children}
    </UIStateContext.Provider>
  );
}

/**
 * Hook to access UI state management functions
 */
export function useUIState() {
  const context = useContext(UIStateContext);
  if (!context) {
    throw new Error('useUIState must be used within a UIStateProvider');
  }
  return context;
}
